<template>
  <div
    class="side-menu bg-white dark:bg-slate-900"
    :class="{ 'is-collapsed': !primaryMenuWithTitles }"
  >
    <div class="side-menu-logo">
      <router-link :to="dashboardPath" replace>
        <img
          :src="primaryMenuWithTitles ? logo : globalConfig.logoThumbnail"
          :alt="globalConfig.installationName"
        />
      </router-link>
    </div>
    <button
      type="button"
      class="side-menu-toggle dark:bg-slate-900 dark:border-slate-800 dark:text-slate-300"
      :aria-label="$t('KEYBOARD_SHORTCUTS.TITLE.TOGGLE_SIDEBAR')"
      :aria-expanded="primaryMenuWithTitles ? 'true' : 'false'"
      @click="togglePrimaryMenuWithTitles"
    >
      <nav-icon
        :src="primaryMenuWithTitles ? collapseIcon : expandIcon"
        :size="16"
      />
    </button>
    <aside class="woot-sidebar side-menu-body">
      <primary-sidebar
        :is-a-custom-branded-instance="isACustomBrandedInstance"
        :menu-items="primaryMenuItems"
        :active-menu-item="activePrimaryMenu.key"
        :with-titles="primaryMenuWithTitles"
        :expandable-keys="expandableKeys"
      >
        <template #default="{ menuKey }">
          <secondary-sidebar
            inline
            :account-id="accountId"
            :inboxes="inboxes"
            :labels="labels"
            :teams="teams"
            :custom-views="customViewsFor(menuKey)"
            :menu-config="secondaryMenuFor(menuKey)"
            :current-role="currentRole"
            :features="features"
            :is-on-chatwoot-cloud="isOnChatwootCloud"
            :show-export-wizard="menuKey === 'settings' && showExportWizard"
            :show-import-wizard="menuKey === 'settings' && showImportWizard"
            @add-label="showAddLabelPopup"
            @toggle-accounts="toggleAccountModal"
          />
        </template>
      </primary-sidebar>
      <secondary-sidebar
        v-if="!primaryMenuWithTitles && showSecondarySidebar"
        :class="sidebarClassName"
        :account-id="accountId"
        :inboxes="inboxes"
        :labels="labels"
        :teams="teams"
        :custom-views="customViews"
        :menu-config="activeSecondaryMenu"
        :current-role="currentRole"
        :features="features"
        :is-on-chatwoot-cloud="isOnChatwootCloud"
        :show-export-wizard="showExportWizard"
        :show-import-wizard="showImportWizard"
        @add-label="showAddLabelPopup"
        @toggle-accounts="toggleAccountModal"
      />
    </aside>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import adminMixin from '../../mixins/isAdmin';
import { getSidebarItems } from './config/default-sidebar';
import alertMixin from 'shared/mixins/alertMixin';

import PrimarySidebar from './sidebarComponents/Primary.vue';
import SecondarySidebar from './sidebarComponents/Secondary.vue';
import NavIcon from './sidebarComponents/NavIcon.vue';
import collapseIcon from 'dashboard/assets/images/sidebar/arrow-left-line.svg';
import expandIcon from 'dashboard/assets/images/sidebar/arrow-right-line.svg';
import { frontendURL } from 'dashboard/helper/URLHelper';
import {
  hasPressedAltAndCKey,
  hasPressedAltAndRKey,
  hasPressedAltAndSKey,
  hasPressedAltAndVKey,
  hasPressedCommandAndForwardSlash,
  isEscape,
} from 'shared/helpers/KeyboardHelpers';
import eventListenerMixins from 'shared/mixins/eventListenerMixins';
import router from '../../routes';
import hasPermissionMixin from 'dashboard/mixins/hasPermission';

const CUSTOM_VIEW_TYPES = {
  conversations: 'conversation',
  contacts: 'contact',
};

export default {
  components: {
    PrimarySidebar,
    SecondarySidebar,
    NavIcon,
  },
  mixins: [adminMixin, alertMixin, eventListenerMixins, hasPermissionMixin],
  props: {
    showSecondarySidebar: {
      type: Boolean,
      default: true,
    },
    sidebarClassName: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      collapseIcon,
      expandIcon,
    };
  },

  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      currentRole: 'getCurrentRole',
      currentUser: 'getCurrentUser',
      globalConfig: 'globalConfig/get',
      inboxes: 'inboxes/getInboxes',
      isACustomBrandedInstance: 'globalConfig/isACustomBrandedInstance',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      isOnChatwootCloud: 'globalConfig/isOnChatwootCloud',
      labels: 'labels/getLabelsOnSidebar',
      teams: 'teams/getMyTeams',
      getAccount: 'accounts/getAccount',
      features: 'getCurrentFeatures', // d99d
      routePermissions: 'getRoutePermissions', // d99d
    }),
    primaryMenuWithTitles() {
      return this.$store.state.uiSettings.primaryMenuWithTitles;
    },
    account() {
      return this.getAccount(this.accountId);
    },
    logo() {
      return this.account === undefined
        ? undefined
        : this.account.logo_url ||
            this.currentUser.logo_user_url ||
            this.globalConfig.logo;
    },
    activeCustomView() {
      return CUSTOM_VIEW_TYPES[this.activePrimaryMenu.key] || '';
    },
    customViews() {
      return this.customViewsFor(this.activePrimaryMenu.key);
    },
    isConversationOrContactActive() {
      return (
        this.activePrimaryMenu.key === 'contacts' ||
        this.activePrimaryMenu.key === 'conversations'
      );
    },
    sideMenuConfig() {
      return getSidebarItems(this.accountId);
    },
    iPracticomFeatures() {
      return this.account.platform_features?.iPracticom;
    },
    primaryMenuItems() {
      const menuItems = this.sideMenuConfig.primaryMenu;
      return menuItems.filter(menuItem => {
        if (menuItem.key === 'docal' && !this.isDocalEnabled()) return false;
        if (menuItem.key === 'forms') return this.isFormsEnabled();
        if (
          this.iPracticomFeatures &&
          ['changelog', 'help'].includes(menuItem.key)
        ) {
          return false;
        }
        const isAvailableForTheUser = menuItem.roles.includes(this.currentRole);
        const isNewRole = this.$store.getters['getInboxPermissions'].length > 0;
        let routePermitted = this.routePermissions.includes(
          menuItem.toStateName
        );
        // admin and agent roles can't have permissions
        if (isNewRole && !routePermitted && menuItem.toStateName !== 'home') {
          return false;
        }
        if (!isNewRole && !isAvailableForTheUser) {
          return false;
        }
        if (
          menuItem.alwaysVisibleOnChatwootInstances &&
          !this.isACustomBrandedInstance
        ) {
          return true;
        }
        if (menuItem.featureFlag) {
          return this.isFeatureEnabledonAccount(
            this.accountId,
            menuItem.featureFlag
          );
        }
        return true;
      });
    },
    activeSecondaryMenu() {
      const { secondaryMenu } = this.sideMenuConfig;
      const { name: currentRoute } = this.$route;

      const activeSecondaryMenu =
        secondaryMenu.find(menuItem =>
          menuItem.routes.includes(currentRoute)
        ) || {};
      return activeSecondaryMenu;
    },
    activePrimaryMenu() {
      const routeName = this.$route.name;
      if (routeName && routeName.startsWith('settings_integrations')) {
        const integrationsMenu = this.primaryMenuItems.find(item => item.key === 'integrations');
        if (integrationsMenu) return integrationsMenu;
      }

      const activePrimaryMenu =
        this.primaryMenuItems.find(
          menuItem => menuItem.key === this.activeSecondaryMenu.parentNav
        ) || {};
      return activePrimaryMenu;
    },
    expandableKeys() {
      return this.sideMenuConfig.secondaryMenu
        .filter(menu => menu.menuItems && menu.menuItems.length)
        .map(menu => menu.parentNav);
    },
    dashboardPath() {
      return frontendURL(`accounts/${this.accountId}/dashboard`);
    },
    showExportWizard() {
      return this.activePrimaryMenu.key === 'settings' && (this.isAdmin || this.hasCreatePermission('Export Wizard'));
    },
    showImportWizard() {
      return this.activePrimaryMenu.key === 'settings' && (this.isAdmin || this.hasCreatePermission('Import Wizard'));
    },
  },

  watch: {
    activeCustomView() {
      this.fetchCustomViews();
    },
  },
  mounted() {
    this.$store.dispatch('labels/get');
    this.$store.dispatch('inboxes/get');
    this.$store.dispatch('notifications/unReadCount');
    this.$store.dispatch('teams/get');
    this.$store.dispatch('attributes/get');
    this.fetchSidebarCustomViews();
  },

  methods: {
    togglePrimaryMenuWithTitles() {
      this.$store.commit('uiSettings/SET_PRIMARY_MENU_WITH_TITLES', !this.primaryMenuWithTitles)
    },
    secondaryMenuFor(key) {
      return (
        this.sideMenuConfig.secondaryMenu.find(
          menu => menu.parentNav === key
        ) || {}
      );
    },
    customViewsFor(menuKey) {
      const filterType = CUSTOM_VIEW_TYPES[menuKey];
      return filterType
        ? this.$store.getters['customViews/getCustomViewsByFilterType'](
            filterType
          )
        : [];
    },
    fetchCustomViews() {
      if (this.isConversationOrContactActive) {
        this.$store.dispatch('customViews/get', this.activeCustomView);
      }
    },
    fetchSidebarCustomViews() {
      this.primaryMenuItems
        .filter(item => CUSTOM_VIEW_TYPES[item.key])
        .forEach(item =>
          this.$store.dispatch('customViews/get', CUSTOM_VIEW_TYPES[item.key])
        );
    },
    toggleKeyShortcutModal() {
      this.$emit('open-key-shortcut-modal');
    },
    closeKeyShortcutModal() {
      this.$emit('close-key-shortcut-modal');
    },
    handleKeyEvents(e) {
      if (hasPressedCommandAndForwardSlash(e)) {
        this.toggleKeyShortcutModal();
      }
      if (isEscape(e)) {
        this.closeKeyShortcutModal();
      }

      if (hasPressedAltAndCKey(e)) {
        if (!this.isCurrentRouteSameAsNavigation('home')) {
          router.push({ name: 'home' });
        }
      } else if (hasPressedAltAndVKey(e)) {
        if (!this.isCurrentRouteSameAsNavigation('contacts_dashboard')) {
          router.push({ name: 'contacts_dashboard' });
        }
      } else if (hasPressedAltAndRKey(e)) {
        if (!this.isCurrentRouteSameAsNavigation('settings_account_reports')) {
          router.push({ name: 'settings_account_reports' });
        }
      } else if (hasPressedAltAndSKey(e)) {
        if (!this.isCurrentRouteSameAsNavigation('agent_list')) {
          router.push({ name: 'agent_list' });
        }
      }
    },
    isCurrentRouteSameAsNavigation(routeName) {
      return this.$route.name === routeName;
    },
    toggleAccountModal() {
      this.$emit('toggle-account-modal');
    },
    showAddLabelPopup() {
      this.$emit('show-add-label-popup');
    },
    isDocalEnabled() {
      return this.account?.additional_attributes?.document_signature_enabled;
    },
    isFormsEnabled() {
      return !!this.account?.forms_enabled;
    },
  },
};
</script>

<style lang="scss">
@import '~dashboard/assets/scss/variables';

// d99d
.scroll-invisible {
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.side-menu {
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: 233px;
  height: 100%;
  box-shadow: 4px 0 4px rgba(163, 163, 163, 0.1);

  &.is-collapsed {
    width: auto;
    min-width: 90px;
  }

  [dir='rtl'] & {
    box-shadow: -4px 0 4px rgba(163, 163, 163, 0.1);
  }
}

.side-menu-logo {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  height: 80px;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e5e5;
  border-inline-end: 1px solid #e5e5e5;

  img {
    height: 48px;
    width: auto;
    max-width: 100%;
    object-fit: contain;
  }
}

.side-menu-toggle {
  position: absolute;
  top: 64px;
  inset-inline-start: 211px;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 8px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(17, 24, 39, 0.1);
  color: #6b7280;

  .is-collapsed & {
    inset-inline-start: 67px;
  }

  [dir='rtl'] & .nav-icon {
    transform: scaleX(-1);
  }
}

.side-menu-body {
  display: flex;
  flex: 1;
  min-height: 0;

  > div {
    border-inline-start: 1px solid #e5e5e5;
  }
}

.side-menu-main {
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  padding: 24px 20px;

  &.is-collapsed {
    flex: none;
    width: 90px;
    padding: 16px 20px;
  }
}

.side-menu-list {
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin: 0;
  list-style: none;
}

.side-menu-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  height: 44px;
  padding: 10px 12px;
  border-radius: 4px;
  color: #4b5563;
  font-size: 16px;
  line-height: 24px;

  > .nav-icon,
  > svg,
  > i {
    color: #6b7280;
  }

  &:hover {
    background: #f3f4f6;
    color: #4b5563;
  }

  &.is-active {
    background: #f0fdf4;
    color: #164b35;

    > .nav-icon,
    > svg,
    > i {
      color: #164b35;
    }
  }

  &.is-icon-only {
    justify-content: center;
    width: 48px;
  }
}

.side-menu-chevron {
  display: flex;
  margin-inline-start: auto;
  color: #6b7280;
  transition: transform 0.15s ease;

  &.is-open {
    transform: rotate(180deg);
  }
}

.side-menu-submenu {
  padding-block: 12px;
  padding-inline-start: 24px;
}

.side-menu-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px;
  color: #6b7280;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
}

.side-menu-children {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;
  list-style: none;
}

.side-menu-child {
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  min-width: 0;
  height: 32px;
  padding: 4px;
  border-radius: 4px;
  color: #4b5563;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;

  > .nav-icon,
  > svg,
  > i {
    color: #6b7280;
  }

  &:hover {
    background: #f3f4f6;
    color: #4b5563;
  }

  &.is-active {
    background: #f0fdf4;
    color: #164b35;

    > .nav-icon,
    > svg,
    > i {
      color: #164b35;
    }
  }
}

.side-menu-count {
  margin-inline-start: auto;
  padding: 0 4px;
  border-radius: 4px;
  background: #f3f4f6;
  color: #4b5563;
  font-size: 10px;
  line-height: 16px;

  &.is-zero {
    color: #9ca3af;
  }
}

.dark {
  .side-menu-logo,
  .side-menu-body > div {
    border-color: #1e293b;
  }

  .side-menu-item,
  .side-menu-child {
    color: #f1f5f9;

    &:hover {
      background: #1e293b;
      color: #f1f5f9;
    }

    &.is-active {
      background: #1e293b;
      color: #4ade80;
    }
  }
}
//.

.account-selector--modal {
  .modal-container {
    width: 40rem;
  }
}

.secondary-sidebar {
  overflow-y: auto;
  height: 100%;
}

.account-selector {
  cursor: pointer;
  padding: $space-small $space-large;

  .selected--account {
    margin-top: -$space-smaller;

    & + .account--details {
      padding-left: $space-normal - $space-micro;
    }
  }

  .account--details {
    padding-left: $space-large + $space-smaller;
  }

  &:last-child {
    margin-bottom: $space-large;
  }

  a {
    align-items: center;
    cursor: pointer;
    display: flex;

    .account--name {
      cursor: pointer;
      font-size: $font-size-medium;
      font-weight: $font-weight-medium;
      line-height: 1;
    }

    .account--role {
      cursor: pointer;
      font-size: $font-size-mini;
      text-transform: capitalize;
    }
  }
}

.app-context-menu {
  align-items: center;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  height: 6rem;
}

.current-user--options {
  font-size: $font-size-big;
  margin-bottom: auto;
  margin-left: auto;
  margin-top: auto;
}

.secondary-menu .nested.vertical.menu {
  margin-left: var(--space-small);
}
</style>
